import {
  Table,
  TableColumn,
  TableFilter,
  Progress,
  ResponseErrorPanel,
  MarkdownContent,
} from '@backstage/core-components';
import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import {
  GithubActionsApi,
  PipelineRun,
  GithubActionsApiRef,
} from '../../types';
import { useEntity } from '@backstage/plugin-catalog-react';
import { getCompoundEntityRef } from '@backstage/catalog-model';
import { makeStyles } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

type DenseTableProps = {
  pipelines: PipelineRun[];
};

const useStyles = makeStyles(theme => ({
  link: {
    color: 'rgb(0, 116, 204)',
  },
  wrapper: {
    maxWidth: '100%',
    '& *': {
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 0,
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  accordionDetails: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
}));

function getFilters(): TableFilter[] {
  return [
    {
      column: 'Branch',
      type: 'multiple-select',
    },
    {
      column: 'Triggered By',
      type: 'multiple-select',
    },
    {
      column: 'Status',
      type: 'multiple-select',
    },
  ];
}

function getStatus(githubStatus: string): [string, string] {
  let statusEmoji = '🟡';
  let status = githubStatus.replace(/[_-]/g, ' '); // We get rid of any underscores and hyphens that GitHub Action puts there
  status = status[0].toUpperCase() + status.slice(1); // We make sure that the first character is in capital

  switch (status) {
    case 'Success': {
      statusEmoji = '🟢';
      break;
    }
    case 'Failure': {
      statusEmoji = '🛑';
      break;
    }
    case 'Cancelled': {
      statusEmoji = '🚫';
      break;
    }
    case 'Waiting': {
      statusEmoji = '⏳';
      break;
    }
    default:
      break;
  }

  return [statusEmoji, status];
}

// Drawing the actual Data Table with the desired colum width
export const DenseTable = ({ pipelines }: DenseTableProps) => {
  const classes = useStyles();

  const columns: TableColumn[] = [
    { title: 'Triggered At', field: 'timestamp', width: '10%' },
    { title: 'Latest Update At', field: 'latest_change', width: '10%' },
    { title: 'Workflow Name', field: 'workflow_name', width: '15%' },
    { title: 'Triggered By', field: 'triggered_by', width: '10%' },
    { title: 'Branch', field: 'branch', width: '10%' },
    { title: 'Link', field: 'link', width: '10%' },
    { title: 'Traces', field: 'traces', width: '10%' },
    { title: 'Status', field: 'status', width: '10%' },
    { title: 'Description', field: 'description', width: '25%' },
  ];

  // Setting up which columns should appear on the filter options
  const filters = getFilters();

  // Depending on the outcome of a pipeline run, we want to present a different coloured dot for easier visual browsing
  const data = pipelines.map(pipeline => {
    const [statusEmoji, status] = getStatus(pipeline.status);

    const calculateDaysDifference = (triggerDateString: string): number => {
      const triggerDate = new Date(triggerDateString);
      const currentDate = new Date();

      const differenceInMilliseconds =
        currentDate.getTime() - triggerDate.getTime();

      // Always need to round up, otherwise traces can't be seen
      return Math.ceil(differenceInMilliseconds / (1000 * 60 * 60 * 24));
    };

    const updateTraceIntervalPicker = (triggerDateString: string): string => {
      const daysDifference = calculateDaysDifference(triggerDateString);
      const traceIntervalPicker = `,"traceIntervalPicker":("groupValue":"P${daysDifference}D","customValue":null))`;
      return traceIntervalPicker;
    };

    const traces = `[{"k": "github-run-id", "t": 10, "v": ${pipeline.id}}]`;
    const jsonTraces = JSON.stringify(traces);
    const customEncode = (str: string) =>
      encodeURIComponent(str).replace(/'/g, '%27').replace(/"/g, '%22');
    const encodedTraces = customEncode(jsonTraces);
    const traceIntervalPicker = updateTraceIntervalPicker(pipeline.timestamp);
    const tracesLink = `https://console.cloud.google.com/traces/list?pageState=(%22traceFilter%22:(%22chips%22:${
      encodedTraces
    })${traceIntervalPicker}&project=aura-comp-machinery-traces`;

    // The workflow's name will redirect on a click to the actual YAML file.
    const yamlLink = `https://github.com/neo-technology/neo4j-cloud/tree/${
      pipeline.branch
    }/.github/workflows/${pipeline.link.replace(/^.*[\\/]/, '')}`;

    const descriptionNewLineIndex = pipeline.description.indexOf('\n');
    const descriptionSummary = pipeline.description.substring(
      0,
      descriptionNewLineIndex,
    );
    const descriptionBody = pipeline.description
      .substring(descriptionNewLineIndex)
      .trim();
    const descriptionReact =
      descriptionNewLineIndex > 0 ? (
        <Accordion style={{ width: '100%', boxShadow: 'none' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <MarkdownContent content={descriptionSummary} />
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <MarkdownContent content={descriptionBody} />
          </AccordionDetails>
        </Accordion>
      ) : (
        <MarkdownContent content={pipeline.description} />
      );

    return {
      timestamp: pipeline.timestamp,
      latest_change: pipeline.latest_change,
      triggered_by: pipeline.triggered_by,
      workflow_name: (
        <a target="_BLANK" className={classes.link} href={yamlLink}>
          {' '}
          {pipeline.workflow_name}
        </a>
      ),
      description: descriptionReact,
      branch: pipeline.branch,
      link: (
        <a target="_BLANK" href={pipeline.html_url}>
          {' '}
          <img
            src="../../../../images/octocat.png"
            alt="GitHub Actions links"
            width="35"
            height="35"
          />{' '}
        </a>
      ),
      traces: (
        <a target="_BLANK" className={classes.link} href={tracesLink}>
          {' '}
          traces
        </a>
      ),
      status: `${statusEmoji} ${status}`,
    };
  });

  return (
    <Box style={{ width: '100%', display: 'table', tableLayout: 'fixed' }}>
      <div style={{ width: '100%', overflowX: 'auto', display: 'block' }}>
        <Table
          options={{ search: false, paging: false }}
          columns={columns}
          data={data}
          filters={filters}
          initialState={{ filtersOpen: true, filters: { Branch: ['master'] } }}
        />
      </div>
    </Box>
  );
};

// Fetching data into the Data table
export const GithubPipelinesTable = () => {
  const GithubActionsApi: GithubActionsApi = useApi(GithubActionsApiRef);
  const currentEntity = useEntity();
  const entityName = getCompoundEntityRef(currentEntity.entity);

  const { value, loading, error } = useAsync(async (): Promise<
    PipelineRun[]
  > => {
    const response = await GithubActionsApi.ListWorkflowRuns(entityName.name);
    response.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    return response;
  }, []);

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return <DenseTable pipelines={value || []} />;
};
